/**
 * Diff Export Hook
 *
 * Exports a multi-sheet Excel workbook containing:
 * - Version A (all rows)
 * - Version B (all rows)
 * - Added rows (present in A but not B)
 * - Removed rows (present in B but not A)
 *
 * Uses server-side generation via DuckDB API for consistent architecture
 * with other export formats.
 *
 * @module features/tables/hooks/useDiffExport
 */

import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import { toast } from '@/lib/toast';
import { triggerDownload } from '@/lib/bulkDownload';
import { useRpc, extractErrorMessage } from '@/lib/rpc';

// ============================================================================
// TYPES
// ============================================================================

export interface DiffExportOptions {
  tableId: string;
  fromVersionId: string; // Version B (older)
  toVersionId: string; // Version A (newer)
  tableName: string;
}

interface TokenResponse {
  token: string;
  url: string;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to export diff data as multi-sheet Excel workbook
 *
 * Uses server-side generation via DuckDB API:
 * 1. CF Worker generates signed token with R2 paths
 * 2. Client calls DuckDB API with token
 * 3. DuckDB API queries all 4 datasets and generates workbook
 * 4. Binary file is returned and downloaded
 *
 * @example
 * ```tsx
 * const { mutate: exportDiff, isPending } = useDiffExport();
 *
 * <Button onClick={() => exportDiff({ tableId, fromVersionId, toVersionId, tableName })}>
 *   {isPending ? 'Exporting...' : 'Export to Excel'}
 * </Button>
 * ```
 */
export function useDiffExport(): UseMutationResult<void, Error, DiffExportOptions> {
  const rpc = useRpc();

  return useMutation({
    mutationFn: async (options: DiffExportOptions) => {
      const { tableId, fromVersionId, toVersionId, tableName } = options;

      // 1. Get signed token from CF Worker for xlsx export
      const tokenRes = await rpc.api.private.query.$post({
        json: {
          diff: {
            tableId,
            left: fromVersionId, // Version B (older)
            right: toVersionId, // Version A (newer)
          },
          format: 'xlsx',
        },
      });

      if (!tokenRes.ok) {
        throw new Error(await extractErrorMessage(tokenRes, 'Failed to get export token'));
      }

      const { token, url } = (await tokenRes.json()) as TokenResponse;

      // 2. Call DuckDB API with token to get the workbook
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to generate workbook');
        throw new Error(errorText);
      }

      // 3. Download the binary response
      const blob = await response.blob();

      // 4. Generate filename and trigger download
      const dateStr = new Date().toISOString().split('T')[0];
      const safeTableName = (tableName || 'table').replace(/[^a-z0-9_-]/gi, '_');
      const filename = `${safeTableName}_comparison_${dateStr}.xlsx`;

      triggerDownload(blob, filename);
    },
    onError: (error) => {
      toast.error('Export failed', error instanceof Error ? error.message : 'An unexpected error occurred');
    },
  });
}
